import type { PracticeArea, Role } from '../types/practiceArea';
import { apiErrorDetail } from './apiError';
// The read side lives in components/common/navGating.ts (readActiveRole) —
// one reader for the role; this module owns the writes.

/**
 * The per-tab workspace ROLE (sessionStorage, sent as X-Active-Role by the
 * axios interceptor) and the "chooser done this session" flag — one home for
 * the keys and the write, so the login chooser, the matter page's "switch and
 * open" and the new-tab restore can never disagree about what "entering a
 * workspace" writes.
 *
 * Two storage areas on purpose (see memory: role vs lens are two keys):
 *   - sessionStorage holds the ROLE, per tab — a tab's data scope must not
 *     change under it because another tab switched.
 *   - localStorage holds `LAST_ROLE_KEY`: the last role chosen and the
 *     sign-in it was chosen under. It only ever PRESELECTS / restores within
 *     the same sign-in; it never decides the role for a new login.
 */
export const ACTIVE_ROLE_KEY = 'nyayabodh_active_role';
export const CHOSEN_KEY = 'nyayabodh_practice_area_chosen';
export const LAST_ROLE_KEY = 'nyayabodh_last_role';

export type InAppRole = Extract<Role, 'criminal' | 'civil'>;

/** Representative lens per in-app role (matches the chooser's cards). */
export const ROLE_LENS: Readonly<Record<InAppRole, PracticeArea>> = {
  criminal: 'criminal',
  civil: 'civil_consumer',
};

/** The other direction of `ROLE_LENS` — a matter's own `practice_area` to
 *  the billing `Role`/vertical ('criminal' | 'civil' | 'corporate') that
 *  owns its credit-pack catalog. Everything that isn't 'criminal' or
 *  'corporate' collapses to 'civil' (civil_property, civil_consumer,
 *  family, tax, labor, other, and an absent practice_area on a legacy row)
 *  — there is no separate billing vertical for those finer categories.
 *  Used wherever a matter-scoped surface (e.g. MatterChatPanel) needs the
 *  matter's OWN vertical rather than the session-wide active role, which
 *  can disagree with it (a lawyer with matters in more than one vertical). */
export function verticalForMatter(practiceArea: PracticeArea | null | undefined): Role {
  if (practiceArea === 'criminal' || practiceArea === 'corporate') return practiceArea;
  return 'civil';
}

interface LastRole {
  role: InAppRole;
  /** Firebase `metadata.lastSignInTime` of the sign-in the role was chosen in. */
  signIn: string | null;
}

export function readLastRole(): LastRole | null {
  try {
    const raw = localStorage.getItem(LAST_ROLE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<LastRole>;
    if (v.role !== 'criminal' && v.role !== 'civil') return null;
    return { role: v.role, signIn: typeof v.signIn === 'string' ? v.signIn : null };
  } catch {
    return null;
  }
}

/**
 * Enter a workspace in THIS tab: role + chosen flag (session only). Does NOT
 * touch LAST_ROLE — that stamp lets a new tab of the same sign-in skip the
 * chooser AND the TrialGate, so it is written only once the gate has
 * actually finished (rememberLastRole, from the chooser's TrialGate onDone).
 * An in-session switch (matter page / tool notice) must not stamp it either:
 * it never ran the gate for that role. Storage errors are swallowed.
 */
export function enterRole(role: InAppRole): void {
  try {
    sessionStorage.setItem(ACTIVE_ROLE_KEY, role);
    sessionStorage.setItem(CHOSEN_KEY, '1');
  } catch {
    /* ignore */
  }
}

/** Record the role chosen in this sign-in — call ONLY after the TrialGate
 *  for that role has completed (review S8). */
export function rememberLastRole(role: InAppRole, signIn: string | null): void {
  try {
    localStorage.setItem(LAST_ROLE_KEY, JSON.stringify({ role, signIn } as LastRole));
  } catch {
    /* ignore */
  }
}

/**
 * The workspace a matter lives in, when a matter fetch failed with the
 * backend's 403 `{code: 'wrong_workspace', role}`; null for any other error.
 * The ONE reader of that payload — every surface that loads a matter by id
 * (matter page, context banner, prefill hook, tool pages) branches on it, so
 * none of them can fall back to "not found" for a matter the lawyer owns.
 */
export function wrongWorkspaceRole(err: unknown): Role | null {
  const detail = apiErrorDetail(err);
  if (detail?.code !== 'wrong_workspace') return null;
  const role = detail.role;
  return role === 'criminal' || role === 'civil' || role === 'corporate' ? role : null;
}
